import {
  Button,
  ButtonGroup,
  closeCurrentDialog,
  DataStatus,
  DialogFooter,
  openDialog,
} from 'platform/components';

import {useState} from 'react';

import {isNotNil} from 'ramda';
import {noop} from 'ramda-adjunct';

import {useGetParticipationQuery} from '@dms/api/core';
import {useGetCustomerV2Query} from '@dms/api/customer';
import {
  RenderedTemplateResponseBodyTestDriveNew,
  useCreateTestDriveDocumentMutation,
  useGetBusinessCaseQuery,
} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {
  DocumentsDialog,
  DocumentTemplateBox,
  handleApiError,
  TemplateBoxTemplate,
  useDocumentTemplates,
  usePermissions,
} from '@dms/shared';

interface TestDriveDocumentDialogProps {
  testDriveId: string;
  businessCaseId: string;
  customerId: string;
}

export function TestDriveDocumentDialog(props: TestDriveDocumentDialogProps) {
  const [documentTemplate, setDocumentTemplate] = useState<TemplateBoxTemplate | null>(null);

  const [getDocumentTemplates, {isLoading, isError}] = useDocumentTemplates();
  const {defaultTemplates, selectedTemplates, selectedAuthProfileLabel, primaryTemplate} =
    getDocumentTemplates('test-drive');

  const {data: customer} = useGetCustomerV2Query({customerId: props.customerId});
  const preferredLocale = customer?.preferredLocale;

  const findByPreferredLocale = (templates: TemplateBoxTemplate[]) =>
    isNotNil(preferredLocale)
      ? templates.find((template) => template.locale === preferredLocale)
      : undefined;

  const localePreferredTemplate =
    findByPreferredLocale(selectedTemplates) ?? findByPreferredLocale(defaultTemplates);
  const effectiveDefaultTemplate = localePreferredTemplate ?? primaryTemplate;

  const {data: businessCaseData} = useGetBusinessCaseQuery({businessCaseId: props.businessCaseId});
  const {data: businessCaseParticipation} = useGetParticipationQuery({
    resourceId: EntityResourceIds.businessCase,
    recordId: props.businessCaseId,
  });

  const [hasReadTestDriveDocumentPermission] = usePermissions({
    permissionKeys: ['readTestDriveDocument'],
    scopes: {
      readTestDriveDocument: {
        participation: businessCaseParticipation,
        branchId: businessCaseData?.branchId,
      },
    },
  });

  const [createTestDriveDocument, {isLoading: isCreatingTestDriveDocument}] =
    useCreateTestDriveDocumentMutation();

  const handleCreateTestDriveProtocol = () => {
    createTestDriveDocument({
      createTestDriveDocumentRequestBody: {
        testDriveId: props.testDriveId,
        templateId: documentTemplate?.id ?? effectiveDefaultTemplate?.id ?? '',
      },
    })
      .unwrap()
      .then((response) => {
        closeCurrentDialog();
        return handleDocumentGenerated(response);
      })
      .catch(handleApiError);
  };

  const handleDocumentGenerated = (renderedTemplate: RenderedTemplateResponseBodyTestDriveNew) => {
    const documents = [
      {
        id: renderedTemplate.fileId,
        filename: i18n.t('entity.testDrive.labels.protocolFileName') + '.docx',
        pdfFilename: i18n.t('entity.testDrive.labels.protocolFileName') + '.pdf',
        fileUri: renderedTemplate.url,
        pdfUri: renderedTemplate.pdfUrl,
      },
    ];

    openDialog(
      <DocumentsDialog
        customerId={props.customerId}
        resourceId={EntityResourceIds.businessCase}
        recordId={props.businessCaseId}
        documents={documents}
        text={i18n.t('entity.testDrive.labels.protocolCreated')}
        isDocxFormat={true}
        permissions={{
          hasReadTestDriveDocumentPermission,
        }}
      />,
      {
        title: i18n.t('entity.testDrive.labels.testDriveProtocol'),
        buttons: [
          {
            variant: 'secondary',
            title: i18n.t('general.actions.close'),
            onClick: closeCurrentDialog,
          },
        ],
      }
    );
  };

  return (
    <DataStatus isLoading={isLoading} isError={isError}>
      <DocumentTemplateBox
        onSelect={noop}
        type="test-drive"
        title={i18n.t('entity.testDrive.labels.testDriveProtocol')}
        templates={defaultTemplates}
        authorizationTemplates={selectedTemplates}
        authorizationProfileLabel={selectedAuthProfileLabel}
        preferredLocale={preferredLocale}
        isSelected={true}
        selectedTemplate={documentTemplate ?? effectiveDefaultTemplate ?? null}
        noteLabel={i18n.t('entity.testDrive.labels.protocolNote')}
        onTemplateChange={setDocumentTemplate}
        onNoteChange={noop}
        isWithNote={false}
      />

      <DialogFooter>
        <ButtonGroup align="right">
          <Button
            variant="secondary"
            title={i18n.t('general.actions.cancel')}
            onClick={closeCurrentDialog}
          />
          <Button
            variant="primary"
            title={i18n.t('general.actions.create')}
            onClick={handleCreateTestDriveProtocol}
            isLoading={isCreatingTestDriveDocument}
          />
        </ButtonGroup>
      </DialogFooter>
    </DataStatus>
  );
}
